/** SE drag + two-finger pinch — grow messenger; snap to expanded past threshold. */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefCallback,
} from "react";
import { setMessengerSizeMode } from "@/lib/messages/messengerSizePreference";

const DEFAULT_W = 720;
const DEFAULT_H = 640;
const MIN_W = 360;
const MIN_H = 320;
/** Fraction of viewport that snaps contracted → expanded. */
const EXPAND_W_FRAC = 0.82;
const EXPAND_H_FRAC = 0.78;

export type MessengerContractedSize = { width: number; height: number };

export function defaultMessengerContractedSize(): MessengerContractedSize {
  if (typeof window === "undefined") return { width: DEFAULT_W, height: DEFAULT_H };
  return {
    width: Math.min(DEFAULT_W, window.innerWidth - 24),
    height: Math.min(DEFAULT_H, window.innerHeight - 32),
  };
}

function touchDistance(a: Touch, b: Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function maybeSnapExpanded(width: number, height: number): boolean {
  if (typeof window === "undefined") return false;
  return width >= window.innerWidth * EXPAND_W_FRAC && height >= window.innerHeight * EXPAND_H_FRAC;
}

function clampSize(width: number, height: number): MessengerContractedSize {
  const maxW = Math.max(MIN_W, window.innerWidth - 24);
  const maxH = Math.max(MIN_H, window.innerHeight - 32);
  return {
    width: Math.min(maxW, Math.max(MIN_W, width)),
    height: Math.min(maxH, Math.max(MIN_H, height)),
  };
}

export function useMessengerContractedResize(
  enabled: boolean,
  options?: { onSnapExpand?: () => void },
) {
  const [size, setSize] = useState<MessengerContractedSize>(defaultMessengerContractedSize);
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const onSnapExpandRef = useRef(options?.onSnapExpand);
  onSnapExpandRef.current = options?.onSnapExpand;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const dragRef = useRef<{
    startX: number;
    startY: number;
    startW: number;
    startH: number;
  } | null>(null);

  const pinchRef = useRef<{
    startDist: number;
    startW: number;
    startH: number;
  } | null>(null);

  const [panelEl, setPanelEl] = useState<HTMLElement | null>(null);

  const applySize = useCallback((next: MessengerContractedSize) => {
    const clamped = clampSize(next.width, next.height);
    setSize(clamped);
    if (maybeSnapExpanded(clamped.width, clamped.height)) {
      dragRef.current = null;
      pinchRef.current = null;
      if (onSnapExpandRef.current) onSnapExpandRef.current();
      else setMessengerSizeMode("expanded");
      return true;
    }
    return false;
  }, []);

  const applySizeRef = useRef(applySize);
  applySizeRef.current = applySize;

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (!enabled) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      const current = sizeRef.current;
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startW: current.width,
        startH: current.height,
      };
    },
    [enabled],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (!drag || !enabled) return;
      // Anchored bottom-right panel: drag SE → grow.
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const snapped = applySize({ width: drag.startW + dx, height: drag.startH + dy });
      if (snapped) {
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
      }
    },
    [applySize, enabled],
  );

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLButtonElement>) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  // Native non-passive listeners so pinch can preventDefault (block browser zoom).
  useEffect(() => {
    if (!panelEl || !enabled) return;

    const onTouchStart = (e: TouchEvent) => {
      if (!enabledRef.current || e.touches.length !== 2) {
        if (e.touches.length < 2) pinchRef.current = null;
        return;
      }
      const current = sizeRef.current;
      pinchRef.current = {
        startDist: Math.max(1, touchDistance(e.touches[0], e.touches[1])),
        startW: current.width,
        startH: current.height,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      const pinch = pinchRef.current;
      if (!pinch || !enabledRef.current || e.touches.length !== 2) return;
      e.preventDefault();
      const dist = Math.max(1, touchDistance(e.touches[0], e.touches[1]));
      const scale = dist / pinch.startDist;
      applySizeRef.current({
        width: pinch.startW * scale,
        height: pinch.startH * scale,
      });
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) pinchRef.current = null;
    };

    panelEl.addEventListener("touchstart", onTouchStart, { passive: true });
    panelEl.addEventListener("touchmove", onTouchMove, { passive: false });
    panelEl.addEventListener("touchend", onTouchEnd, { passive: true });
    panelEl.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      panelEl.removeEventListener("touchstart", onTouchStart);
      panelEl.removeEventListener("touchmove", onTouchMove);
      panelEl.removeEventListener("touchend", onTouchEnd);
      panelEl.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, panelEl]);

  const pinchPanelRef = useCallback<RefCallback<HTMLElement>>((node) => {
    setPanelEl(node);
  }, []);

  return {
    size,
    setSize,
    pinchPanelRef,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
}

export function MessengerDragExpandHandle(props: {
  handleProps: ReturnType<typeof useMessengerContractedResize>["handleProps"];
}) {
  return (
    <button
      type="button"
      aria-label="Drag to expand messenger"
      title="Drag to expand"
      className="absolute bottom-0 right-0 z-30 flex h-8 w-8 cursor-se-resize touch-none items-end justify-end rounded-tl-lg border-l border-t border-teal-400 bg-teal-100 shadow-sm dark:border-teal-500 dark:bg-teal-950"
      {...props.handleProps}
    >
      <span
        aria-hidden
        className="mb-1.5 mr-1.5 block h-3 w-3 border-b-2 border-r-2 border-teal-700 dark:border-teal-200"
      />
    </button>
  );
}
